import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Table, Label, Icon, Segment, Grid, Message, Checkbox, Button } from "semantic-ui-react";
import PhotoEditingImage from "~/models/PhotoEditing/PhotoEditingImage";
import TimeOfDay from "~/constants/PhotoEditing/TimeOfDay";
import Location from "~/constants/PhotoEditing/Location";
import PromiseButton from "~/components/element/PromiseButton";
import JobType from "~/constants/PhotoEditing/JobType";
import ImageWithLoading from "~/components/ImageWithLoading";
import Immersive3dTour from "~/models/Immersive3dTour/Immersive3dTour";

@observer
class ImagesDetails extends Component {
  constructor(props) {
    super(props);
    this.downloadRef = React.createRef();
  }

  render() {
    const {
      photoEditingJobTypeId,
      photoEditingImages,
      downloadProcessedImagesUrl,
      importImagesDisabled,
      toggleSelectionCallback,
      toggleAllSelectionCallback,
      imageClickCallback,
      importCallback,
      importInImmersive3dTourCallback,
      actionCallback,
      immersive3dTour,
      importInImmersive3dTourDisabled,
    } = this.props;

    // find selected images, ignore images not ready.
    const selectedImages = photoEditingImages.filter(({ isReady, isSelected }) => isReady && isSelected);
    const selectedImagesCount = selectedImages.length;
    const hasSelectedImages = selectedImagesCount > 0;
    const selectedImageIds = selectedImages.map(({ photoEditingImageId }) => photoEditingImageId);

    const notReadyImages = photoEditingImages.filter(({ isReady }) => !isReady);
    const hasNotReadyImages = notReadyImages.length > 0;

    return (
      <Table columns={2} className="images-details">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell textAlign="center" colSpan={2}>
              <Checkbox
                className="toggle-all-images"
                // Only take into account ready images, since the every image checkbox won't show up
                // for pending images.
                checked={photoEditingImages.filter(({ isReady }) => isReady).every(({ isSelected }) => isSelected)}
                onChange={() => toggleAllSelectionCallback()}
              />
              <form ref={this.downloadRef} method="get" action={downloadProcessedImagesUrl}>
                <Button.Group>
                  {importImagesDisabled ? null : (
                    <>
                      <PromiseButton
                        primary
                        disabled={!hasSelectedImages || hasNotReadyImages}
                        onClick={() => importCallback()}
                      >
                        Import {importInImmersive3dTourDisabled ? "selected images " : null}({selectedImagesCount})
                      </PromiseButton>
                      <Button.Or />
                    </>
                  )}
                  <PromiseButton
                    secondary
                    type="submit"
                    disabled={!hasSelectedImages}
                    onClick={() => {
                      // By returning a promise here we'll trigger the button to show a spinner simulating
                      // a network connection so the user has a visual feedback that something is happening.
                      // The actual download triggers pretty much immediately and it's not obvious that clicking the
                      // button is doing something.
                      return new Promise(resolve => {
                        this.downloadRef.current.submit();
                        // the timeout time here is arbitrary.
                        setTimeout(resolve, 1000);
                      });
                    }}
                  >
                    Download {importInImmersive3dTourDisabled ? "selected images " : null}({selectedImagesCount})
                  </PromiseButton>
                  {importImagesDisabled || importInImmersive3dTourDisabled ? null : (
                    <>
                      <Button.Or />
                      <Button
                        primary
                        disabled={
                          !hasSelectedImages ||
                          hasNotReadyImages ||
                          // If there is an Immersive 3D Tour
                          (immersive3dTour &&
                            // If it has new panoramas data, it means it is processing
                            (immersive3dTour.hasNewPanoramasData ||
                              // Post-processing
                              // Instead of asking if the Tour Job is done
                              // we check the isReady which is changed to true
                              // when the TourJob is done
                              (immersive3dTour.isProcessed && !immersive3dTour.isReady) ||
                              // Or if it has been denied
                              immersive3dTour.isDenied))
                        }
                        onClick={() => importInImmersive3dTourCallback(selectedImageIds)}
                      >
                        Import in Immersive 3D Tour ({selectedImagesCount})
                      </Button>
                    </>
                  )}
                </Button.Group>
                <input
                  type="hidden"
                  name="only-image-ids"
                  value={selectedImages.map(({ photoEditingImageId }) => photoEditingImageId).join(",")}
                />
              </form>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {photoEditingImages.map((photoEditingImage, imageIndex) => (
            <Table.Row key={photoEditingImage.photoEditingImageId} columns={2}>
              <Table.Cell verticalAlign="top" className="one-third-wide">
                <Label>Image # {imageIndex + 1}</Label>
                {photoEditingImage.isReady ? (
                  <SelectableImage
                    photoEditingImage={photoEditingImage}
                    toggleSelectionCallback={toggleSelectionCallback}
                    onClickCallback={() => imageClickCallback(imageIndex)}
                  />
                ) : (
                  <PlaceholderImage />
                )}
              </Table.Cell>
              <Table.Cell className="two-third-wide" textAlign="center">
                {photoEditingJobTypeId === JobType.HD_REAL ? (
                  <div>
                    <TimeOfDayLabel photoEditingImage={photoEditingImage} />
                    <LocationLabel photoEditingImage={photoEditingImage} />
                    <br />
                    <br />
                  </div>
                ) : null}
                {photoEditingImage.messages.map((message, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Message key={index} warning>
                    <p>{message.text}</p>
                  </Message>
                ))}
                {photoEditingImage.refundDts !== null ? (
                  <Message positive>
                    <p>The credits for this image have been refunded!</p>
                  </Message>
                ) : null}
                <Grid centered>
                  {photoEditingImage.photoEditingPhotos.map(photoEditingPhoto => (
                    <Grid.Column key={photoEditingPhoto.photoEditingPhotoId} className="grid-column-tiny-image">
                      <ImageWithLoading
                        src={photoEditingPhoto.thumbnailUrl || photoEditingPhoto.url}
                        size="tiny"
                        width="78"
                        height="52"
                      />
                    </Grid.Column>
                  ))}
                </Grid>
                {actionCallback && photoEditingJobTypeId === JobType.HD_REAL && photoEditingImage.refundDts === null ? (
                  <PromiseButton primary onClick={() => actionCallback(photoEditingImage.photoEditingImageId)}>
                    Refund Image
                  </PromiseButton>
                ) : null}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  }
}

ImagesDetails.propTypes = {
  photoEditingJobTypeId: PropTypes.number.isRequired,
  photoEditingImages: PropTypes.arrayOf(PropTypes.instanceOf(PhotoEditingImage)),
  downloadProcessedImagesUrl: PropTypes.string.isRequired,
  importImagesDisabled: PropTypes.bool,
  toggleSelectionCallback: PropTypes.func.isRequired,
  toggleAllSelectionCallback: PropTypes.func.isRequired,
  imageClickCallback: PropTypes.func.isRequired,
  importCallback: PropTypes.func.isRequired,
  importInImmersive3dTourCallback: PropTypes.func,
  actionCallback: PropTypes.func,
  importInImmersive3dTourDisabled: PropTypes.bool,
  immersive3dTour: PropTypes.instanceOf(Immersive3dTour),
};

export default ImagesDetails;

// sub components

// An image that can be selected

const SelectableImage = observer(({ photoEditingImage, toggleSelectionCallback, onClickCallback }) => (
  <div className="image-wrapper">
    <ImageWithLoading
      src={photoEditingImage.thumbnailUrl || photoEditingImage.url}
      size="small"
      width="150"
      height="100"
      onClickCallback={() => onClickCallback()}
    />
    <Checkbox
      checked={photoEditingImage.isSelected}
      onChange={() => toggleSelectionCallback(photoEditingImage.photoEditingImageId)}
    />
  </div>
));

SelectableImage.propTypes = {
  photoEditingImage: PropTypes.instanceOf(PhotoEditingImage),
  toggleSelectionCallback: PropTypes.func.isRequired,
  onClickCallback: PropTypes.func.isRequired,
};

// A placeholder of an image that hasn't been processed yet

const PlaceholderImage = () => (
  <Segment placeholder>
    <Grid columns={1} stackable textAlign="center">
      <Grid.Row verticalAlign="middle">
        <Grid.Column>
          <Icon name="clock outline" />
          In Progress
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </Segment>
);

// Time of Day label with day/night icon

const TimeOfDayLabel = observer(({ photoEditingImage }) => (
  <Label>
    <Icon name={photoEditingImage.timeOfDay === TimeOfDay.NIGHT_TIME ? "moon" : "sun"} />
    {photoEditingImage.timeOfDayLabel}
  </Label>
));

TimeOfDayLabel.propTypes = {
  photoEditingImage: PropTypes.instanceOf(PhotoEditingImage),
};

// Location label with indoors/outdoors icon

const LocationLabel = observer(({ photoEditingImage }) => (
  <Label>
    <Icon name={photoEditingImage.location === Location.EXTERIOR ? "tree" : "bed"} />
    {photoEditingImage.locationLabel}
  </Label>
));

LocationLabel.propTypes = {
  photoEditingImage: PropTypes.instanceOf(PhotoEditingImage),
};
